/* eslint-disable import/no-cycle */
import API from '@/APIs/API';
import AthenaAPI from '@/APIs/AthenaAPI';
import CRM from '@/APIs/CRM';
import {SsnForm} from '@/APIs/types';
import type {AgentStore} from '@/Agent/AgentStore';
import {User} from '@/Auth/User';
import type {CaseManagerStore} from '@/CaseManager/CaseManagerStore';
import Utils from '@/Core/Utils';
import {Approval} from '@/Core/mobx/ParameterStore/Approval';
import {approvalGuards} from '@/Core/mobx/ParameterStore/ApprovalGuards';
import {fieldCategory, fieldOptions as approvalFieldOptions} from '@/Core/mobx/ParameterStore/ApprovalXref';
import {Categories} from '@/Core/mobx/ParameterStore/types';
import {healthGuards} from '@/Core/mobx/ParameterStore/HealthGuards';
import {fieldOptions as healthFieldOptions} from '@/Core/mobx/ParameterStore/HealthXref';
import {Parameter} from '@/Core/mobx/ParameterStore/ParameterStore';
import {prequalGuards} from '@/Core/mobx/ParameterStore/PrequalGuards';
import {fieldOptions as prequalFieldOptions} from '@/Core/mobx/ParameterStore/PrequalXref';
import {quoteGuards} from '@/Core/mobx/ParameterStore/QuoteGuards';
import {fieldOptions as quoteFieldOptions} from '@/Core/mobx/ParameterStore/QuoteXref';
import RootStore from '@/Core/mobx/RootStore';
import {makeAutoObservable, runInAction} from 'mobx';
import moment from 'moment';
import {v4} from 'uuid';

type LoadFormParams = {
    id: string;
    email: string;
    historyId?: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Form = any;

export class ContactFormStore {
    id = '';

    historyId: string | null = null;

    name = '';

    contact = '';

    email = '';

    form: Form = null;

    hs_object_id = '';

    hubspot_owner_id = '';

    formCreatedAt: Date | null = null;

    constructor(private parentStore: AgentStore | CaseManagerStore) {
        makeAutoObservable(this);
    }

    async selectForm({id, email, historyId}: LoadFormParams) {
        this.id = id;
        this.email = email;
        this.historyId = historyId ?? null;
        this.name = '';
        this.contact = '';
        this.form = null;

        const form = await this.getForm({id, email, historyId});

        if (!form) return null;

        await this.parentStore.parameterStore.loadParamsFromSavedForm(form);

        this.name = form.name;
        this.contact = form.contact;
        this.formCreatedAt = form.formCreatedAt;
        this.hs_object_id = form.hubspot?.hs_object_id ?? '';
        this.hubspot_owner_id = form.hubspot?.hubspot_owner_id ?? '';
        this.form = form;

        return form;
    }

    async startFamilyForm({id, email}: LoadFormParams) {
        this.email = email;

        const form = await this.getForm({id, email});

        if (!form) return null;

        // Set current date for new family form creation date
        form.formCreatedAt = new Date();
        await this.parentStore.parameterStore.loadParamsFromSavedForm(form, {onlyBasic: true});

        this.name = form.name;
        this.contact = form.contact;
        this.form = form;
        this.hs_object_id = form.hubspot?.hs_object_id ?? '';
        this.hubspot_owner_id = form.hubspot?.hubspot_owner_id ?? '';

        return form;
    }

    async getForm({id, email, historyId}: {id: string; email: string; historyId?: string}) {
        /* prettier-ignore */
        return historyId
            ? CRM.GetClientHistoryRecord(email, historyId)
            : CRM.GetClientRecord(email, id);
    }

    async getSsnFormsFromSsnData(ssn: string | null): Promise<SsnForm[]> {
        if (!ssn) return [];

        const response = await AthenaAPI.getSsnForms({ssn});

        if (!response) {
            throw new Error('Failed to verify not a duplicate SSN');
        }

        return response;
    }

    async getSsnFormsAssociatedWithCurrentFormSsn(): Promise<SsnForm[]> {
        const {parameterStore} = RootStore.agentPortalStore;
        const ssn = parameterStore?.approval?.socialSecurityNumber.value;
        return this.getSsnFormsFromSsnData(ssn);
    }

    setFormId(uuid: string) {
        const {parameterStore} = RootStore.agentPortalStore;
        if (parameterStore.formid == null || parameterStore.formid == '') {
            runInAction(() => {
                parameterStore.formid = uuid;
            });
        }
    }

    async saveForm(formType?: 'prequal' | 'quote' | 'preapproval') {
        const newSelectedCustomer = RootStore.agentPortalStore.customerPortalStore.selectedCustomer;

        const {parameterStore} = RootStore.agentPortalStore;

        // if (!parameterStore.contact) {
        //     console.log('Form does not have a contact email and cannot be saved.');
        //     return;
        // }

        const sform: any = {};

        sform.formid = parameterStore.formid;
        sform.contact = parameterStore.contact;
        sform.name = parameterStore.name;
        sform.createTimestamp = parameterStore.createTimestamp;
        sform.created = parameterStore.created;

        sform.formPreQual = parameterStore.formPreQual;
        sform.formQuote = parameterStore.formQuote;
        sform.formPreApproval = parameterStore.formPreApproval;
        sform.formProsperUWStatus = parameterStore.formProsperUWStatus;
        sform.formProsperCaseStartDate = parameterStore.formProsperCaseStartDate;
        sform.prequalCurrentStep = parameterStore.prequalCurrentStep;
        sform.prosperRequest = parameterStore.prosperRequest;
        sform.prosperDecision = parameterStore.prosperDecision;
        sform.prosperDeclineReason = parameterStore.prosperDeclineReason;

        sform.applicationId = parameterStore.applicationId;
        sform.instantIulApplicationId = parameterStore.instantIulApplicationId;
        sform.instantIulWorkedOnByAgent = parameterStore.instantIulWorkedOnByAgent;
        sform.instantIulStartedByCustomer = parameterStore.instantIulStartedByCustomer;
        sform.dealId = parameterStore.dealId;
        sform.eAppSubmitted = parameterStore.eAppSubmitted;
        sform.eAppApproved = parameterStore.eAppApproved;
        sform.eAppDeclined = parameterStore.eAppDeclined;
        sform.eAppReferredToManualUW = parameterStore.eAppReferredToManualUW;
        sform.eAppDocusignCompleted = parameterStore.eAppDocusignCompleted;
        sform.instantIulMultiQuestion = parameterStore.instantIulMultiQuestion;

        sform.rxRecords = parameterStore.rxRecords;
        sform.ssnData = parameterStore.ssnData;

        sform.submitted = parameterStore.submitted;

        sform.recommendedProductsResponse = parameterStore.recommendedProductsResponse;

        sform.hubspot = {
            hs_object_id: this.hs_object_id,
            hubspot_owner_id: this.hubspot_owner_id,
        };
        if (sform.formid == null || sform.formid == '') {
            sform.formid = v4();
            runInAction(() => {
                parameterStore.formid = sform.formid;
            });
        }
        if (sform.contact == null || sform.contact == '') {
            sform.contact = this.contact;
            runInAction(() => {
                parameterStore.contact = sform.contact;
            });
        }
        if (sform.name == null || sform.name == '') {
            sform.name = this.name;
            runInAction(() => {
                parameterStore.name = sform.name;
            });
        }
        if (sform.agent == null || sform.agent == '') {
            sform.agent = User.Info.email;
            runInAction(() => {
                parameterStore.agent = sform.agent;
            });
        }
        if (sform.created == null || sform.created == '') {
            sform.created = Utils.Date2Str(new Date());
            runInAction(() => {
                parameterStore.created = sform.created;
            });
        }
        if (!sform.createTimestamp) {
            sform.createTimestamp = new Date().valueOf();
            runInAction(() => {
                parameterStore.createTimestamp = sform.createTimestamp;
            });
        }
        sform.updated = Utils.Date2Str(new Date());
        runInAction(() => {
            parameterStore.updated = sform.updated;
            parameterStore.updatedTimestamp = moment().toISOString();
        });

        sform.parameters = Object.keys(parameterStore.prequal).map((n) => {
            return {p: n, v: parameterStore.prequal[n as keyof typeof parameterStore.prequal].value};
        });
        sform.health = Object.keys(parameterStore.health).map((n) => {
            return {p: n, v: parameterStore.health[n as keyof typeof parameterStore.health].value};
        });
        sform.quote = Object.keys(parameterStore.quote)
            .filter((n) => n != 'isCombination')
            .map((n) => {
                return {p: n, v: (parameterStore.quote[n as keyof typeof parameterStore.quote] as Parameter).value};
            });
        sform.quoteCombination = Object.keys(parameterStore.quoteCombination)
            .filter((n) => n != 'isCombination')
            .map((n) => {
                return {
                    p: n,
                    v: (parameterStore.quoteCombination[n as keyof typeof parameterStore.quoteCombination] as Parameter)
                        .value,
                };
            });
        sform.quoteHistory = parameterStore.quoteHistory;
        sform.banking = Object.keys(parameterStore.banking).map((n) => {
            return {p: n, v: parameterStore.banking[n as keyof typeof parameterStore.banking].value};
        });

        sform.approval = Object.keys(parameterStore.approval).map((n) => {
            return {p: n, v: parameterStore.approval[n as keyof typeof parameterStore.approval].value};
        });

        // Compute readyForSubmission: true when all required non-banking fields are filled
        const approvalEntries: [string, Parameter][] = Object.entries(parameterStore.approval)
            .filter(([n]) => n !== 'calculatedContNumberOfBenes')
            .filter(
                ([n]) =>
                    !approvalGuards.hasOwnProperty(n) ||
                    approvalGuards[n as keyof typeof approvalGuards](parameterStore),
            ) as [string, Parameter][];

        let readyForSubmission = true;
        for (const [name, param] of approvalEntries) {
            const category = fieldCategory.hasOwnProperty(name)
                ? fieldCategory[name as keyof typeof fieldCategory]
                : undefined;
            if (category === Categories.BANKING_INFORMATION) continue;

            const required = Approval.isRequiredField(
                name,
                parameterStore.approval.agentProducts.value,
                parameterStore.approval.existingCoverage.value,
                parameterStore.approval.hasPrimaryCarePhysician.value,
                parameterStore.approval.fgMarijuanaPast5Years.value,
                false,
                !parameterStore.approval.conditionalNextAvailable.value,
            );

            if (required && (param.value === null || param.value === undefined || param.value === '')) {
                readyForSubmission = false;
                break;
            }
        }
        sform.approval.push({p: 'readyForSubmission', v: readyForSubmission});

        // Debug: log address fields being saved
        const addrFields = sform.approval.filter((f: any) =>
            [
                'address',
                'addressCity',
                'state',
                'addressZipCode',
                'addressUnit',
                'clientAddressVerified',
                'clientAddressVerifiedSnapshot',
            ].includes(f.p),
        );

        // Save AB flags with the form
        sform.abFlags = parameterStore.abFlags;
        if (Object.keys(parameterStore.abFlags).length > 0) {
            console.log(`Saving AB flags with form ${sform.formid}:`, parameterStore.abFlags);
        }

        // Save HHI warning info with the form
        sform.hhiWarningInfo = parameterStore.hhiWarningInfo;

        if (newSelectedCustomer && newSelectedCustomer?.id !== sform.customerPortalCustomerID) {
            sform.customerPortalCustomerID = newSelectedCustomer?.id;
        }

        const firstName = parameterStore.approval.firstName.value;
        const lastName = parameterStore.approval.lastName.value;

        sform.formCreatedAt = parameterStore.formCreatedAt;
        sform.formInitiallySubmittedAt = parameterStore.formInitiallySubmittedAt;

        const clientResponse = await CRM.SetClientRecord(sform.contact, sform.formid, sform);

        if (clientResponse) {
            await API.Call('agent-activity', 'POST', {
                key: sform.formid,
                data: {
                    action: 'form-saved',
                    form: sform.formid,
                    agent: User.Info.email,
                    name: sform.name,
                    contact: sform.contact,
                    applicantFirstName: firstName,
                    applicantLastName: lastName,
                    formPreQual: sform.formPreQual,
                    formQuote: sform.formQuote,
                    formPreApproval: sform.formPreApproval,
                    formSubmitted: sform.submitted,
                    formCreatedAt: sform.formCreatedAt,
                    formInitiallySubmittedAt: sform.formInitiallySubmittedAt,
                },
            });
        }

        API.LogToMixPanel('agent-form-saved', {
            agent: User.Info.email,
            customer: sform.name,
            contact: sform.contact,
            formid: sform.formid,
        });

        const ssn = parameterStore.approval.socialSecurityNumber.value;

        if (ssn) {
            await AthenaAPI.associateSsnWithForm({
                ssn,
                form: {
                    email: sform.contact,
                    formId: sform.formid,
                },
            });
        }

        if (formType) {
            runInAction(() => {
                parameterStore.unmarkFormDirty(formType);
            });
        }

        return {
            contact: sform.contact,
            formid: sform.formid,
        };
    }

    async submitForm(formType: 'prequal' | 'quote' | 'preapproval') {
        try {
            const {parameterStore} = RootStore.agentPortalStore;
            if (parameterStore && !parameterStore.formInitiallySubmittedAt && formType === 'preapproval')
                parameterStore.formInitiallySubmittedAt = new Date();

            await this.saveForm(formType);
            const data = {} as {[key: string]: Parameter['value']};
            Object.keys(parameterStore.prequal)
                .filter((p) => {
                    return (
                        !prequalGuards.hasOwnProperty(p) ||
                        prequalGuards[p as keyof typeof prequalGuards](parameterStore)
                    );
                })
                .forEach((d) => {
                    let val = parameterStore.prequal[d as keyof typeof parameterStore.prequal].value;
                    if (prequalFieldOptions.hasOwnProperty(d))
                        val =
                            prequalFieldOptions[d as keyof typeof prequalFieldOptions].options.find(
                                (o) => Utils.MakeVarName(o) === val,
                            ) ?? val;
                    data[d] = val;
                });
            Object.keys(parameterStore.health)
                .filter((p) => {
                    return (
                        !healthGuards.hasOwnProperty(p) || healthGuards[p as keyof typeof healthGuards](parameterStore)
                    );
                })
                .forEach((d) => {
                    let val = parameterStore.health[d as keyof typeof parameterStore.health].value;
                    if (healthFieldOptions.hasOwnProperty(d))
                        val =
                            (healthFieldOptions[d as keyof typeof healthFieldOptions].options as any[]).find(
                                (o) => Utils.MakeVarName(o.toString()) === val?.toString(),
                            ) ?? val;
                    data[d] = val;
                });
            Object.keys(parameterStore.approval)
                .filter((p) => {
                    return (
                        !approvalGuards.hasOwnProperty(p) ||
                        approvalGuards[p as keyof typeof approvalGuards](parameterStore)
                    );
                })
                .forEach((d) => {
                    let val = parameterStore.approval[d as keyof typeof parameterStore.approval].value;
                    if (approvalFieldOptions.hasOwnProperty(d))
                        val =
                            approvalFieldOptions[d as keyof typeof approvalFieldOptions].options.find(
                                (o) => Utils.MakeVarName(o) === val,
                            ) ?? val;
                    data[d] = val;
                });
            Object.keys(parameterStore.quote)
                .filter((p) => {
                    return (
                        !quoteGuards.hasOwnProperty(p) ||
                        quoteGuards[p as keyof typeof quoteGuards](parameterStore, parameterStore.quote)
                    );
                })
                .forEach((d) => {
                    if (d != 'isCombination') {
                        let val = (parameterStore.quote[d as keyof typeof parameterStore.quote] as Parameter).value;
                        if (quoteFieldOptions.hasOwnProperty(d))
                            val =
                                quoteFieldOptions[d as keyof typeof quoteFieldOptions].options.find(
                                    (o) => Utils.MakeVarName(o) === val,
                                ) ?? val;
                        data[d] = val;
                    }
                });
            if (parameterStore.quoteCombination.isCombination) {
                Object.keys(parameterStore.quoteCombination)
                    .filter((p) => {
                        return (
                            !quoteGuards.hasOwnProperty(p) ||
                            quoteGuards[p as keyof typeof quoteGuards](parameterStore, parameterStore.quoteCombination)
                        );
                    })
                    .forEach((d) => {
                        if (d != 'isCombination') {
                            let val = (
                                parameterStore.quoteCombination[
                                    d as keyof typeof parameterStore.quoteCombination
                                ] as Parameter
                            ).value;
                            if (quoteFieldOptions.hasOwnProperty(d))
                                val =
                                    quoteFieldOptions[d as keyof typeof quoteFieldOptions].options.find(
                                        (o) => Utils.MakeVarName(o) === val,
                                    ) ?? val;
                            data[d] = val;
                        }
                    });
            }
            const bdsName = parameterStore.approval.bdsName.value;
            data['bdsName'] = this.parentStore.getAgentsList().find((o) => Utils.MakeVarName(o) === bdsName) ?? bdsName;
            data['contact'] = parameterStore.contact;
            data['agent'] = parameterStore.approval.agentEmail.value || User.Info.email;
            data['name'] = parameterStore.name;
            data['customer'] = parameterStore.name;
            data['formid'] = parameterStore.formid;
            data['updated'] = parameterStore.updated;
            data['created'] = parameterStore.created;
            data['datetime'] = new Date().toUTCString();
            data['policyOwnerName'] =
                parameterStore.approval.policyOwnerName.value || data['firstName'] + ' ' + data['lastName'];
            data.hubspot = {
                hs_object_id: this.hs_object_id,
                hubspot_owner_id: this.hubspot_owner_id,
            };
            data.formCreatedAt = parameterStore.formCreatedAt;
            data.formInitiallySubmittedAt = parameterStore.formInitiallySubmittedAt;

            runInAction(() => {
                // @ts-ignore
                parameterStore.submitted = true;
            });

            // save the form (before submission)

            // trigger API call
            await API.Call('zapier', 'POST', data);

            API.LogToMixPanel('agent-form-preapproval-submitted', data);

            RootStore.uiStore.showSnackbar('Form successfully submitted', 'success');
        } catch (e) {
            console.log(e);
        }
    }
}
